'use client';
import { FC, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Select,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Tabs,
  Tab,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import CollaboratorsTab from './CollaboratorsTab';
import { useBackend } from '@/hooks/useBackend';
import { PrivilegeEnum } from '@/api/ApiClient';
import { formatDate } from '@/lib/utils/dateUtils';
import ProjectFooter from '@/components/details/ProjectFooter';
import DetailField from '@/components/common/DetailField';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { log } from '@/lib/utils/logger';
import { filterAutocompleteOptionsByMatchRank } from '@/lib/utils/autocompleteMatchRank';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useTree } from '@/hooks/useTree';
import { useTreeStore } from '@/stores/treeStore';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { useDeleteEntity } from '@/hooks/useDeleteEntity';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useProjectData } from '@/hooks/useTreeDetailSubscriptions';
import { usePrivileges } from '@/hooks/usePrivileges';
import { getPrivilegeForEntity } from '@/types/tree';
import UserLink, { UserLinks } from '@/components/common/UserLink';
import { isHtmlContentEmpty } from '@/lib/utils/textUtils';
import { getProjectDeleteDisabledReason } from '@/lib/utils/projectDeleteAuth';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { toUpdateProjectRequest } from '@/lib/entity/entitySaveUtils';
import {
  canShowProjectCollaboratorsTab,
  normalizeProjectUIModel,
  ProjectUIModel,
  toProjectUIModel,
} from '@/lib/models/projectUIModel';
import { useAuthStore } from '@/stores/authStore';
import { formatUsJurisdictionForDisplay } from '@/lib/constants/usJurisdictionOptions';

export const selectOptions: OptionKey[] = [
  'researcherOptions',
  'facultyOptions',
  'projectType',
  'projectCategory',
  'projectStatus',
  'materials',
  'synthesisTechniques',
];

const ProjectDetails: FC = () => {
  const { options, loading: optionsLoading } = useSelectOptions(selectOptions);
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const { resolveUser, resolveUsers } = useUserResolution();
  const { setDataModified, updateDataPartial, updateData, isDataModified } = useTree();
  const { deleteEntity } = useDeleteEntity();
  const { selectedNode } = useTreeSelection();
  const { isAuthorized } = usePrivileges();
  const currentUserId = useAuthStore((s) => s.currentUser?.id);

  const [isReadOnly, setIsReadOnly] = useState(true); // Start in read-only mode
  const [tabIndex, setTabIndex] = useState(0);
  // Keep Details/Collaborators mounted after first load so invalidating users/staff
  // (Add new User) does not tear down nested dialogs and leave a stuck MUI backdrop.
  const [hasLoadedOptionsOnce, setHasLoadedOptionsOnce] = useState(false);
  if (!optionsLoading && !hasLoadedOptionsOnce) {
    setHasLoadedOptionsOnce(true);
  }

  const project = useProjectData(selectedNode);

  // Reactive subscription to modifiedData state for hasChanges
  const hasChanges = useTreeStore((state) => (selectedNode ? state.modifiedData.has(selectedNode.dataId) : false));

  // Validation state
  const [projectLabelError, setProjectLabelError] = useState(false);
  const [typeError, setTypeError] = useState(false);
  const [awardInstitutionError, setAwardInstitutionError] = useState(false);
  const [awardStateError, setAwardStateError] = useState(false);
  const [awardTypeError, setAwardTypeError] = useState(false);
  const [userPIError, setUserPIError] = useState(false);
  const [facultyError, setFacultyError] = useState(false);
  const [titleError, setTitleError] = useState(false);
  // Award geography is a frozen snapshot (filled from PI on first submit). Only rewrite it when the
  // admin explicitly edits those fields — not on every save (collaborators/title/etc.).
  const [awardGeographyDirty, setAwardGeographyDirty] = useState(false);

  const isInternalProject = !!project?.type?.isInternal;
  // User PI is not editable from the UI.
  // It is independent from Faculty selection (changing Faculty must not overwrite PI).

  const validateProject = (p: ProjectUIModel, opts?: { requireAwardSnapshotCorrection?: boolean }) => {
    const hasProjectLabel = !!p.projectLabel?.trim();
    const hasType = !!p.type?.id;
    // Institution type (category) is required for external projects at create/edit.
    const requiresCategory = !p.type?.isInternal;
    // Institution/state at award are only required when correcting the frozen snapshot.
    const requiresAwardSnapshotCorrection = Boolean(opts?.requireAwardSnapshotCorrection) && requiresCategory;

    const hasCategory = !requiresCategory || !!p.category;
    const hasAwardInstitution = !requiresAwardSnapshotCorrection || !!p.piInstitution?.trim();
    const hasAwardState = !requiresAwardSnapshotCorrection || !!p.piState?.trim();
    const hasUserPI = !!p.piUserId;
    const hasFaculty = !!p.facultyUserIds && p.facultyUserIds.length > 0;
    const hasTitle = !!p.title && !isHtmlContentEmpty(p.title);

    const issues: string[] = [];
    if (!hasProjectLabel) issues.push('Project ID');
    if (!hasType) issues.push('Type');
    if (!hasAwardInstitution) issues.push('Institution at award');
    if (!hasAwardState) issues.push('State at award');
    if (!hasCategory) issues.push('Institution type');
    if (!hasUserPI) issues.push('PI');
    if (!hasFaculty) issues.push('Faculty');
    if (!hasTitle) issues.push('Title');

    return {
      hasProjectLabel,
      hasType,
      hasCategory,
      hasAwardInstitution,
      hasAwardState,
      hasUserPI,
      hasFaculty,
      hasTitle,
      issues,
      isValid: issues.length === 0,
    };
  };

  // Material options
  const materialOptions = options.materials
    ?.filter((m) => m.id !== undefined && m.name !== undefined && m.name !== 'None')
    .sort((a, b) => a.name!.localeCompare(b.name!))
    .map((m) => ({
      id: m.id!,
      name: m.name!,
    }));

  const convertDateForBackend = (date: Date | null): Date | undefined => {
    return date ? date : undefined;
  };

  const privilege = getPrivilegeForEntity('project', project) ?? PrivilegeEnum.UserProject;
  const canEditProject = isAuthorized(privilege, 'update');
  // FIXME: User.Update is a stand-in for "admin may rewrite frozen award geography."
  // Prefer a dedicated privilege so this is not tied to general user-admin rights.
  const canEditAwardGeography = isAuthorized(PrivilegeEnum.User, 'update');
  const awardGeographyReadOnly = isReadOnly || !canEditAwardGeography;
  const showCollaboratorsTab = canShowProjectCollaboratorsTab(project, { canEditProject, currentUserId });
  const correctingAwardGeography = canEditAwardGeography && awardGeographyDirty && !isInternalProject;

  const handleModification = (modified: Partial<ProjectUIModel>) => {
    if (!selectedNode) return;
    updateDataPartial(selectedNode.dataId, modified);

    // Clear validation errors when user starts typing
    if (modified.projectLabel !== undefined) {
      setProjectLabelError(false);
    }
    if (modified.type !== undefined) {
      setTypeError(false);
      // If switching to internal project type, category is not applicable
      if ((modified.type as any)?.isInternal) {
        setAwardTypeError(false);
        setAwardInstitutionError(false);
        setAwardStateError(false);
        setAwardGeographyDirty(false);
      }
    }
    if (modified.category !== undefined) {
      setAwardTypeError(false);
      setAwardGeographyDirty(true);
    }
    if (modified.piInstitution !== undefined) {
      setAwardInstitutionError(false);
      setAwardGeographyDirty(true);
    }
    if (modified.piState !== undefined) {
      setAwardStateError(false);
      setAwardGeographyDirty(true);
    }
    if (modified.piUserId !== undefined) {
      setUserPIError(false);
    }
    if (modified.facultyUserIds !== undefined) {
      setFacultyError(false);
    }
    if (modified.title !== undefined) {
      setTitleError(false);
    }
  };

  // Validation function
  const isValid = () => {
    if (!project) return true;
    return validateProject(project, { requireAwardSnapshotCorrection: correctingAwardGeography }).isValid;
  };

  const handleSave = async () => {
    if (!project?.id || !selectedNode) return;

    // Validate required fields and set error states
    const validation = validateProject(project, {
      requireAwardSnapshotCorrection: correctingAwardGeography,
    });
    setProjectLabelError(!validation.hasProjectLabel);
    setTypeError(!validation.hasType);
    setAwardInstitutionError(correctingAwardGeography ? !validation.hasAwardInstitution : false);
    setAwardStateError(correctingAwardGeography ? !validation.hasAwardState : false);
    setAwardTypeError(isInternalProject ? false : !validation.hasCategory);
    setUserPIError(!validation.hasUserPI);
    setFacultyError(!validation.hasFaculty);
    setTitleError(!validation.hasTitle);

    if (!validation.isValid) {
      showSnackbar(`Please fill out required fields: ${validation.issues.join(', ')}`, 'error');
      return;
    }

    try {
      const updateRequest = toUpdateProjectRequest(project, {
        // Only rewrite frozen award snapshot when the admin edited those fields this session.
        setAwardGeography: correctingAwardGeography,
      });
      const updatedData = await backend.project.update(project.id, updateRequest);
      updateData(selectedNode.dataId, toProjectUIModel(updatedData));

      setDataModified(selectedNode.dataId, false);
      setAwardGeographyDirty(false);
      setIsReadOnly(true);
      showSnackbar('Project saved successfully', 'success');
    } catch (error) {
      log.error('[ProjectDetails] Error saving project:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to save project.', 'error');
    }
  };

  const handleCancel = async () => {
    if (!project?.id || !selectedNode) return;

    // Check if there are unsaved changes
    const isModified = isDataModified(selectedNode.dataId);

    if (isModified) {
      try {
        // Reload the project data from the server to discard any unsaved changes
        const reloadedData = await backend.project.get(project.id.toString());
        updateData(selectedNode.dataId, toProjectUIModel(reloadedData));

        // Clear the modification state
        setDataModified(selectedNode.dataId, false);
        setAwardGeographyDirty(false);
      } catch (error) {
        log.error('[ProjectDetails] Error reloading project data:', error);
        showSnackbar(getProblemDetail(error) ?? 'Failed to reload project data.', 'error');
        // Fallback: just clear the modification state
        setDataModified(selectedNode.dataId, false);
        setAwardGeographyDirty(false);
      }
    }

    // Return to read-only mode
    setIsReadOnly(true);
  };

  const activeTabIndex = !showCollaboratorsTab && tabIndex === 1 ? 0 : tabIndex;

  if (optionsLoading && !hasLoadedOptionsOnce) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Loading options...
        </Typography>
      </Box>
    );
  }

  if (!project || selectedNode?.type !== 'project') {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          {!project ? 'Select a node to view details' : 'Selected node is not a project.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* Tabs - Fixed at top */}
      <Tabs
        value={activeTabIndex}
        onChange={(_, newValue) => setTabIndex(newValue)}
        aria-label="Project detail tabs"
        sx={{ flexShrink: 0 }}
      >
        <Tab label="Details" />
        {showCollaboratorsTab ? <Tab label="Additional Collaborators" /> : null}
      </Tabs>

      {/* Scrollable Content Area */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {/* Tab Content */}
        {activeTabIndex === 0 ? (
          <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              {/* Project ID, Type, Institution category */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <DetailField
                  label="Project ID"
                  value={project.projectLabel || ''}
                  isReadOnly={isReadOnly}
                  required
                  error={projectLabelError ? 'Project ID is required' : undefined}
                >
                  <Box>
                    <Typography
                      variant="body1"
                      component="span"
                      sx={{
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        display: 'block',
                        color: projectLabelError ? 'error.main' : undefined,
                      }}
                    >
                      {project.projectLabel || ''}
                    </Typography>
                    {projectLabelError ? (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        Project ID is required
                      </Typography>
                    ) : null}
                  </Box>
                </DetailField>

                <DetailField
                  label="Type"
                  value={project.type?.label || ''}
                  isReadOnly={isReadOnly}
                  required
                  error={typeError ? 'Type is required' : undefined}
                >
                  <Box>
                    <Typography
                      variant="body1"
                      component="span"
                      sx={{
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        display: 'block',
                        color: typeError ? 'error.main' : undefined,
                      }}
                    >
                      {project.type?.label || ''}
                    </Typography>
                    {typeError ? (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        Type is required
                      </Typography>
                    ) : null}
                  </Box>
                </DetailField>
              </Box>

              {/* Title */}
              <DetailField
                label="Title"
                value={<span dangerouslySetInnerHTML={{ __html: project.title || '' }} />}
                isReadOnly={isReadOnly}
              >
                <Box>
                  <RichTextEditor
                    value={project.title || ''}
                    onChange={(value) => handleModification({ title: value })}
                  />
                  {titleError ? (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      Title is required
                    </Typography>
                  ) : null}
                </Box>
              </DetailField>

              {/* PI, Project Status, Published */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <DetailField
                  label="PI"
                  value={
                    project.piUserId ? (
                      <UserLink userId={project.piUserId} displayText={resolveUser(project.piUserId)} />
                    ) : (
                      'N/A'
                    )
                  }
                  isReadOnly={isReadOnly}
                  required
                  error={userPIError ? 'PI is required' : undefined}
                >
                  <Box>
                    <Typography
                      variant="body1"
                      component="span"
                      sx={{
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        display: 'block',
                        color: userPIError ? 'error.main' : undefined,
                      }}
                    >
                      {project.piUserId ? resolveUser(project.piUserId) : 'N/A'}
                    </Typography>
                    {userPIError ? (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        PI is required
                      </Typography>
                    ) : null}
                  </Box>
                </DetailField>

                <DetailField
                  label="Project Status"
                  value={
                    options.projectStatus?.find((s) => s.key === project.projectStatus)?.value ||
                    project.projectStatus ||
                    ''
                  }
                  isReadOnly={isReadOnly}
                >
                  <Select
                    value={project.projectStatus || ''}
                    onChange={(e) => handleModification({ projectStatus: e.target.value })}
                    fullWidth
                    size="small"
                  >
                    {options.projectStatus?.map((status) => (
                      <MenuItem key={status.key} value={status.key}>
                        {status.value}
                      </MenuItem>
                    ))}
                  </Select>
                </DetailField>

                {/* Note: In the old Knockout implementation, Published checkbox was hidden for type codes 'ID' and 'IM'.
                  We're skipping that visibility rule here for simplicity. */}
                <DetailField label="Published" value={project.isPublished ? 'Yes' : 'No'} isReadOnly={isReadOnly}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={project.isPublished || false}
                        onChange={(e) => handleModification({ isPublished: e.target.checked })}
                        disabled={isReadOnly}
                      />
                    }
                    label=""
                  />
                </DetailField>
              </Box>

              {!isInternalProject ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <DetailField
                    label="Institution at award"
                    value={project.piInstitution?.trim() || 'N/A'}
                    isReadOnly={awardGeographyReadOnly}
                    required={correctingAwardGeography}
                    error={awardInstitutionError ? 'Institution at award is required' : undefined}
                  >
                    <TextField
                      value={project.piInstitution ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (!value.trim()) return;
                        handleModification({ piInstitution: value });
                      }}
                      fullWidth
                      size="small"
                      disabled={awardGeographyReadOnly}
                      error={awardInstitutionError}
                    />
                  </DetailField>
                  <DetailField
                    label="State at award"
                    value={formatUsJurisdictionForDisplay(project.piState) || project.piState?.trim() || 'N/A'}
                    isReadOnly={awardGeographyReadOnly}
                    required={correctingAwardGeography}
                    error={awardStateError ? 'State at award is required' : undefined}
                  >
                    <TextField
                      value={project.piState ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (!value.trim()) return;
                        handleModification({ piState: value });
                      }}
                      fullWidth
                      size="small"
                      disabled={awardGeographyReadOnly}
                      placeholder="e.g. PA or province code"
                      error={awardStateError}
                    />
                  </DetailField>
                  <DetailField
                    label="Institution type"
                    value={
                      options.projectCategory?.find((c) => c.key === project.category)?.value ||
                      project.institutionType?.trim() ||
                      project.category ||
                      'N/A'
                    }
                    isReadOnly={awardGeographyReadOnly}
                    required
                    error={awardTypeError ? 'Institution type is required' : undefined}
                  >
                    <Select
                      value={project.category ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (!value) return;
                        handleModification({ category: value as ProjectUIModel['category'] });
                      }}
                      fullWidth
                      size="small"
                      disabled={awardGeographyReadOnly}
                    >
                      {options.projectCategory?.map((cat) => (
                        <MenuItem key={cat.key} value={cat.key}>
                          {cat.value}
                        </MenuItem>
                      ))}
                    </Select>
                  </DetailField>
                </Box>
              ) : null}
              {canEditAwardGeography && !isReadOnly && !isInternalProject ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1 }}>
                  Frozen when the project is submitted. Edit here to correct legacy snapshot data.
                </Typography>
              ) : null}

              {/* Faculty and Materials */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <DetailField
                  label="Faculty"
                  value={
                    project.facultyUserIds && project.facultyUserIds.length > 0 ? (
                      <UserLinks
                        userIds={project.facultyUserIds}
                        displayTexts={project.facultyUserIds.filter(Boolean).map((id) => resolveUser(id as string))}
                      />
                    ) : (
                      'N/A'
                    )
                  }
                  isReadOnly={isReadOnly}
                  required
                  error={facultyError ? 'Faculty is required' : undefined}
                >
                  <Autocomplete
                    multiple
                    options={options.facultyOptions ?? []}
                    getOptionLabel={(option) => option?.value || ''}
                    isOptionEqualToValue={(option, value) => option.key === value.key}
                    value={
                      (project.facultyUserIds ?? [])
                        .filter(
                          (facultyId): facultyId is string => typeof facultyId === 'string' && facultyId.trim() !== ''
                        )
                        .map((facultyId) => {
                          const match = (options.facultyOptions ?? []).find((o) => o.key === facultyId);
                          return (
                            match ??
                            ({
                              key: facultyId,
                              value: resolveUser(facultyId),
                            } as any)
                          );
                        }) as any
                    }
                    onChange={(_, newValue) => {
                      handleModification({
                        facultyUserIds: (newValue ?? []).map((v: any) => v.key).filter(Boolean),
                      });
                    }}
                    autoSelect
                    autoHighlight
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        error={facultyError}
                        helperText={facultyError ? 'Faculty is required' : undefined}
                      />
                    )}
                    disabled={isReadOnly}
                  />
                </DetailField>

                <DetailField
                  label="Materials"
                  value={
                    (materialOptions?.filter((opt) => project.materialIds?.includes(opt.id)) || [])
                      .map((i) => i.name)
                      .join(', ') || 'N/A'
                  }
                  isReadOnly={isReadOnly}
                >
                  <Autocomplete
                    multiple
                    options={materialOptions ?? []}
                    getOptionLabel={(option) => option.name}
                    filterOptions={(opts, params) => filterAutocompleteOptionsByMatchRank(opts, params, (o) => o.name)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={materialOptions?.filter((opt) => project.materialIds?.includes(opt.id)) || []}
                    onChange={(_, newValue) =>
                      handleModification({
                        materials: newValue.map((opt) => opt.name),
                        materialIds: newValue.map((opt) => opt.id),
                      })
                    }
                    autoSelect
                    autoHighlight
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                    disabled={isReadOnly}
                  />
                </DetailField>
              </Box>

              {/* Staff and Techniques */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <DetailField
                  label="Staff"
                  value={
                    project.staffUserIds && project.staffUserIds.length > 0 ? (
                      isReadOnly ? (
                        <UserLinks
                          userIds={project.staffUserIds}
                          displayTexts={project.staffUserIds.map((id) => resolveUser(id))}
                        />
                      ) : (
                        resolveUsers(project.staffUserIds)
                      )
                    ) : (
                      'N/A'
                    )
                  }
                  isReadOnly={isReadOnly}
                >
                  <Autocomplete
                    multiple
                    value={options.researcherOptions?.filter((opt) => project.staffUserIds?.includes(opt.key)) || []}
                    onChange={(_, newValue) => handleModification({ staffUserIds: newValue.map((opt) => opt.key) })}
                    options={options.researcherOptions || []}
                    getOptionLabel={(option) => option.value || ''}
                    isOptionEqualToValue={(option, value) => option.key === value.key}
                    autoSelect
                    autoHighlight
                    renderOption={(props, option) => (
                      <li {...props} key={option.key}>
                        {option.value}
                      </li>
                    )}
                    renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                    disabled={isReadOnly}
                  />
                </DetailField>

                <DetailField label="Techniques" value={project.techniques?.join(', ') || 'N/A'} isReadOnly={isReadOnly}>
                  <Autocomplete
                    multiple
                    value={(() => {
                      const allTechniques =
                        options.synthesisTechniques?.filter(
                          (m) => m.id !== undefined && m.name !== undefined && m.name !== 'None'
                        ) || [];
                      // Filter to top-level techniques only
                      const topLevelTechniques = allTechniques.filter((item: any) => {
                        const itemId = item.id;
                        const hasParent = item.parentTechnique || item.parentID || item.parent;
                        if (!hasParent) return true;
                        const isGroup = allTechniques.some((other: any) => {
                          const otherParent = other.parentTechnique || other.parentID || other.parent;
                          return otherParent === itemId;
                        });
                        return isGroup;
                      });
                      return topLevelTechniques.filter((opt) => project.techniques?.includes(opt.id!));
                    })()}
                    onChange={(_, newValue) => handleModification({ techniques: newValue.map((opt) => opt.id!) })}
                    options={(() => {
                      const allTechniques =
                        options.synthesisTechniques?.filter(
                          (m) => m.id !== undefined && m.name !== undefined && m.name !== 'None'
                        ) || [];
                      // Filter to top-level techniques only
                      return allTechniques.filter((item: any) => {
                        const itemId = item.id;
                        const hasParent = item.parentTechnique || item.parentID || item.parent;
                        if (!hasParent) return true;
                        const isGroup = allTechniques.some((other: any) => {
                          const otherParent = other.parentTechnique || other.parentID || other.parent;
                          return otherParent === itemId;
                        });
                        return isGroup;
                      });
                    })()}
                    getOptionLabel={(option) => option.name || ''}
                    autoSelect
                    autoHighlight
                    isOptionEqualToValue={(option, value) => option.name === value.name}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                    disabled={isReadOnly}
                  />
                </DetailField>
              </Box>

              {/* Start Date and End Date */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DetailField
                    label="Start Date"
                    value={project.startDate ? formatDate(project.startDate) : 'N/A'}
                    isReadOnly={isReadOnly}
                  >
                    <DatePicker
                      value={project.startDate ? new Date(project.startDate) : null}
                      onChange={(newValue) => {
                        handleModification({
                          startDate: convertDateForBackend(newValue),
                        });
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                        },
                      }}
                    />
                  </DetailField>
                </LocalizationProvider>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DetailField
                    label="End Date"
                    value={project.endDate ? formatDate(project.endDate) : 'N/A'}
                    isReadOnly={isReadOnly}
                  >
                    <DatePicker
                      value={project.endDate ? new Date(project.endDate) : null}
                      onChange={(newValue) => {
                        handleModification({
                          endDate: convertDateForBackend(newValue),
                        });
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                        },
                      }}
                    />
                  </DetailField>
                </LocalizationProvider>
              </Box>
            </Stack>
          </Paper>
        ) : null}

        {/* Collaborators Tab Content */}
        {showCollaboratorsTab && activeTabIndex === 1 ? (
          <CollaboratorsTab
            project={project}
            isReadOnly={isReadOnly}
            onUpdateCollaborators={(collaborators) => {
              if (selectedNode) {
                // Update only "other collaborators"; full collaborator list is assembled centrally.
                updateDataPartial(
                  selectedNode.dataId,
                  normalizeProjectUIModel({
                    ...project,
                    otherCollaborators: collaborators,
                  })
                );
                // Mark the data as modified
                setDataModified(selectedNode.dataId, true);
                log.debug('[ProjectDetails] Collaborators updated:', collaborators);
              }
            }}
          />
        ) : null}
      </Box>

      {/* Footer - Fixed at bottom */}
      <Box sx={{ flexShrink: 0 }}>
        <ProjectFooter
          lastChangedDate={project.lastChangedDate}
          lastChangedBy={resolveUser(project.lastChangedByUser || '')}
          projectStatus={project.projectStatus ?? ''}
          projectStatusDisplay={
            options.projectStatus?.find((s) => s.key === project.projectStatus)?.value ||
            project.projectStatus ||
            'Unknown'
          }
          isReadOnly={isReadOnly}
          hasChanges={hasChanges}
          onSave={handleSave}
          onDelete={() => deleteEntity(selectedNode!)}
          onCancel={handleCancel}
          setIsReadOnly={setIsReadOnly}
          privilege={privilege}
          isFormValid={isValid()}
          validationIssues={
            project ? validateProject(project, { requireAwardSnapshotCorrection: correctingAwardGeography }).issues : []
          }
          deleteDisabledReason={getProjectDeleteDisabledReason(project) ?? undefined}
        />
      </Box>
    </Box>
  );
};

export default ProjectDetails;
